// frontend/src/components/CookieConsent/consentUtils.js

import { setMarketingConsent } from '../../services/metaPixel';

// Version for consent - increment to force re-consent.
// 1.2: marketing cookies (Meta Pixel) became real - every earlier consent was
// given in a no-marketing-cookies world, so everyone is asked again. A 1.1
// record - even an "accept all" one carrying marketing:true - is ignored by
// getStoredConsent(), which is what keeps the pixel off until the visitor
// answers the new banner. Analytics keeps running on the old answer meanwhile
// (analytics.readStoredAnalyticsConsent is deliberately version-blind); the
// visible side effect is that every returning visitor sees the banner again, and
// those who now answer "essential only" drop out of GA4 / product events.
export const CONSENT_VERSION = '1.2';

// Consent categories and their defaults
export const DEFAULT_PREFERENCES = {
  necessary: true,     // Always true - required for auth, language
  analytics: false,    // GA4 cookies - opt-in required
  marketing: false,    // Meta Pixel - opt-in required
};

// Cookie names to manage by category
export const COOKIE_CATEGORIES = {
  necessary: [
    'token',           // Auth token (stored in localStorage)
    'language',        // Language preference
    'theme',           // Theme preference
    'accessibility',   // Accessibility settings
  ],
  analytics: [
    '_ga',             // GA4 main cookie
    '_ga_*',           // GA4 measurement ID specific
    '_gid',            // GA4 user distinguisher
    '_gat',            // GA4 rate limiting
    'analytics_session_id', // Our session tracking
  ],
  marketing: [
    '_fbp',            // Meta Pixel browser id
    '_fbc',            // Meta Pixel ad-click id (derived from fbclid)
    '_gcl_au',         // Google Ads
  ],
};

// Get stored consent preferences
export const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem('cookieConsent');
    if (!stored) return null;

    const consent = JSON.parse(stored);

    // Check if version matches
    if (consent.version !== CONSENT_VERSION) {
      return null; // Force re-consent on version change
    }

    return consent;
  } catch (error) {
    console.error('Error reading stored consent:', error);
    return null;
  }
};

/**
 * Fired on `window` the moment consent is stored.
 *
 * The consent sheet is `position: fixed; zIndex: 9999` - ABOVE MUI's dialog
 * layer (1300) - so on a phone it covers a dialog's action row outright. Any
 * surface that must wait for the banner needs to know when it has been
 * answered, and polling `localStorage` on a timer to find out is the shape this
 * event replaces. Precedent: `OPEN_SETTINGS_EVENT`.
 *
 * The event carries NO detail: "consent was saved" is the whole signal, and a
 * listener that needs the preferences reads them through `getStoredConsent`.
 */
export const CONSENT_SAVED_EVENT = 'leadify:consent-saved';

// Save consent preferences
export const saveConsent = (preferences) => {
  try {
    const consentData = {
      preferences,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    try {
      window.dispatchEvent(new CustomEvent(CONSENT_SAVED_EVENT));
    } catch {
      // No window, or CustomEvent unavailable (a node-side test). The consent
      // itself is already stored, which is this function's actual job - a
      // listener that misses the event re-evaluates on the next load.
    }
    return true;
  } catch (error) {
    console.error('Error saving consent:', error);
    return false;
  }
};

// Check if consent is required
export const isConsentRequired = () => {
  const stored = getStoredConsent();
  return !stored || stored.version !== CONSENT_VERSION;
};

// Delete cookies by category
export const deleteCookiesByCategory = (category) => {
  const cookies = COOKIE_CATEGORIES[category] || [];

  cookies.forEach(cookieName => {
    // Handle wildcard patterns
    if (cookieName.includes('*')) {
      const prefix = cookieName.replace('*', '');
      // Check if document.cookie exists (for test environment)
      const cookieString = document.cookie || '';
      if (cookieString) {
        cookieString.split(';').forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          if (name && name.startsWith(prefix)) {
            deleteCookie(name);
          }
        });
      }
    } else {
      deleteCookie(cookieName);
    }
  });
};

// Every domain a cookie for this page could live on: the host itself and each
// parent (staging.leadifyai.biz -> leadifyai.biz). Third-party scripts such as
// the Meta Pixel set their cookie on the registrable PARENT domain, so deleting
// only on the current host leaves it behind on any subdomain.
export const cookieDomainsFor = (hostname) => {
  const parts = String(hostname || '').split('.').filter(Boolean);
  if (parts.length === 1) return parts; // localhost
  const domains = [];
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(parts.slice(i).join('.'));
  }
  return domains;
};

// Delete a specific cookie
const deleteCookie = (name) => {
  // Delete for current domain and path
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  // Also try with domain variations, the host and every parent domain
  cookieDomainsFor(window.location.hostname).forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
  });
};

// Apply consent preferences
export const applyConsentPreferences = (preferences) => {
  // Delete cookies for categories that are not consented
  Object.keys(COOKIE_CATEGORIES).forEach(category => {
    if (category !== 'necessary' && !preferences[category]) {
      deleteCookiesByCategory(category);
    }
  });

  // Update analytics consent
  if (window.analytics) {
    window.analytics.updateConsent(preferences.analytics === true);
  }

  // Marketing consent gates the Meta Pixel. It never loads before a grant, and
  // a withdrawal revokes it (its cookies were already deleted above).
  setMarketingConsent(preferences.marketing === true);

  return true;
};

// Get all cookies as object
export const getAllCookies = () => {
  const cookies = {};
  document.cookie.split(';').forEach(cookie => {
    const [name, value] = cookie.split('=').map(s => s.trim());
    if (name) {
      cookies[name] = value;
    }
  });
  return cookies;
};

// Check which categories have active cookies
export const getActiveCookieCategories = () => {
  const activeCookies = Object.keys(getAllCookies());
  const activeCategories = {};

  Object.entries(COOKIE_CATEGORIES).forEach(([category, cookies]) => {
    activeCategories[category] = cookies.some(cookieName => {
      if (cookieName.includes('*')) {
        const prefix = cookieName.replace('*', '');
        return activeCookies.some(active => active.startsWith(prefix));
      }
      return activeCookies.includes(cookieName);
    });
  });

  return activeCategories;
};